import { useMemo } from "react";
import { App } from "./useQueryApps";
import { PublicApp } from "./useQueryPublicApps";
import { PrivateApp } from "./useQueryPrivateApps";
import {
  PrivateImplementationMeta,
  useQueryPrivateImplementationsMeta,
} from "../implementationMeta/useQueryPrivateImplementationsMeta";

interface Input {
  // The mixed-array member supports merged public + private lists (useApps
  // with filterType "all").
  apps: App[] | PublicApp[] | PrivateApp[] | (PublicApp | PrivateApp)[];
  /**
   * When true, filters the returned apps to only those whose `implementationMeta`
   * contains an `authType`. Apps without one are excluded from the result.
   *
   * Note: the filter runs after hydration. If `implementationMeta` has not yet
   * resolved (e.g. still loading, or the selectedApi version doesn't match the
   * latest implementation), an app may be excluded even when it is accessible.
   * Check `implementationMeta.authType` directly and guard on `isLoading` if
   * you need to distinguish "unavailable" from "not yet loaded".
   */
  onlyAppsWithAuth?: boolean;
}

interface AppWithImplementationsMeta extends App {
  implementationMeta?: PrivateImplementationMeta;
}

interface PublicAppWithImplementationsMeta extends PublicApp {
  implementationsMeta?: PrivateImplementationMeta;
}

interface PrivateAppWithImplementationsMeta extends PrivateApp {
  implementationsMeta?: PrivateImplementationMeta;
}

export type { AppWithImplementationsMeta };
export type { PublicAppWithImplementationsMeta };
export type { PrivateAppWithImplementationsMeta };

/** This hook takes an array of apps and hydrates them with
 * an implementation meta object from the `/api/v4/implementations-meta/private endpoint.
 * This object is used for rendering app implementation details like names and images,
 * and determining authentication details for that app
 */

export function useHydrateAppsWithImplementationsMeta({
  apps,
  onlyAppsWithAuth,
}: Input) {
  // Build a unique list of currentImplementationIds from the apps
  const currentImplementationIds = useMemo(() => {
    const auths = apps.reduce(
      (accumulator, app) => {
        const key =
          (app as App)?.currentImplementationId?.split("@")[0] ||
          (app as PublicApp)?.selectedApi?.split("@")[0] ||
          (app as PrivateApp)?.selectedApi?.split("@")[0];

        if (key) {
          accumulator[key] = true;
          return accumulator;
        }

        return accumulator;
      },
      {} as Record<string, boolean>,
    );
    return Object.keys(auths);
  }, [apps]);

  // Query for implementationsMeta for the currentImplementationIds
  const { implementationsMeta, isLoading, isFetching } =
    useQueryPrivateImplementationsMeta(
      {
        selectedApis: currentImplementationIds,
      },
      {
        enabled: currentImplementationIds?.length > 0,
      },
    );

  // Build a hash of the implementationsMeta by currentImplementationId
  const implementationsMetaHash = useMemo(() => {
    return implementationsMeta.reduce(
      (accumulator, imp) => {
        if (!imp.id) {
          return accumulator;
        }
        const key = imp.id.split("@")[0];
        accumulator[key] = imp;
        return accumulator;
      },
      {} as Record<string, PrivateImplementationMeta>,
    );
  }, [implementationsMeta]);

  // Hydrate apps with implementation objects
  let hydratedApps = apps.map((app) => {
    const key =
      (app as App)?.currentImplementationId?.split("@")[0] ||
      (app as PublicApp)?.selectedApi?.split("@")[0] ||
      (app as PrivateApp)?.selectedApi?.split("@")[0];

    return {
      ...app,
      implementationMeta: key ? implementationsMetaHash[key] : undefined,
    };
  });

  // This allows us to optionally filter out apps that don't have an authType, and
  // therefore shouldn't show in the Typeahead when the user creates a new connection
  if (onlyAppsWithAuth) {
    hydratedApps = hydratedApps.filter(
      (app) => app.implementationMeta?.authType,
    );
  }

  return {
    apps: hydratedApps,
    isLoading,
    isFetching,
  };
}
